import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { getOrgPermissions } from '../../utils/permissions';
import {
  getEngagement, getTimeline, fetchDocumentBlob, signDocument, declineDocument, cancelEngagement,
  fetchExecutedBlob, fetchCertificateBlob, downloadBlob, fmtDateTime, DOC_STATUS_LABELS, apiError,
} from './engagementsApi';
import { StatusPill } from './EngagementsPage';
import SignaturePad from './SignaturePad';
import './Engagements.css';

// AWAITING_COUNTERSIGN means the client is done, so "Signed by you" is complete at stage 3.
const STEPS = [
  { key: 'REQUESTED', label: 'Requested' },
  { key: 'ISSUED', label: 'Documents issued' },
  { key: 'SIGNED', label: 'Signed by you' },
  { key: 'COUNTERSIGNED', label: 'Countersigned by Cataam' },
  { key: 'ACTIVE', label: 'Service active' },
];
const ORDER = { REQUESTED: 0, ON_HOLD: 1, ISSUED: 1, AWAITING_COUNTERSIGN: 3, ACTIVE: 5, SUSPENDED: 5, COMPLETED: 5, CANCELLED: -1 };

/** One engagement: status stepper, documents to review/sign, intake summary and the immutable timeline. */
export default function EngagementDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isOrgAdmin, isPartner } = getOrgPermissions();
  const canSign = isOrgAdmin && !isPartner;

  const [e, setE] = useState(null);
  const [events, setEvents] = useState([]);
  const [tab, setTab] = useState('documents');
  const [error, setError] = useState('');
  const [viewer, setViewer] = useState(null);     // { doc, url, blob }
  const [opening, setOpening] = useState(null);   // doc id being fetched
  const [signing, setSigning] = useState(null);   // doc
  const [form, setForm] = useState({ typedName: '', typedTitle: '', consent: false });
  const [signMode, setSignMode] = useState('type');     // 'type' | 'draw'
  const [drawnImage, setDrawnImage] = useState(null);   // PNG data URL
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setError('');
    try {
      const [d, ev] = await Promise.all([getEngagement(id), getTimeline(id)]);
      setE(d);
      setEvents(ev);
    } catch (err) {
      setError(apiError(err, t('Engagements.loadError', 'Could not load the engagement.')));
    }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeViewer = () => {
    if (viewer) window.URL.revokeObjectURL(viewer.url);
    setViewer(null);
  };

  // Fully signed documents open as the executed copy; otherwise the original PDF.
  const view = async (doc, original = false) => {
    setError('');
    setOpening(doc.id);
    try {
      const blob = doc.hasExecutedCopy && !original ? await fetchExecutedBlob(id, doc.id) : await fetchDocumentBlob(id, doc.id);
      if (viewer) window.URL.revokeObjectURL(viewer.url);
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      setViewer({ doc, blob: pdfBlob, url: window.URL.createObjectURL(pdfBlob) });
      load(); // status moves to VIEWED on first open; also refreshes the hash we echo back when signing
    } catch (err) {
      setError(apiError(err, t('Engagements.viewError', 'Could not open the document.')));
    } finally {
      setOpening(null);
    }
  };

  const openSign = (doc) => {
    const p = e?.party || {};
    setForm({ typedName: p.signatoryName || '', typedTitle: p.signatoryTitle || '', consent: false });
    setSignMode('type');
    setDrawnImage(null);
    setSigning(doc);
  };

  const submitSign = async () => {
    setBusy(true);
    setError('');
    try {
      const fresh = await getEngagement(id);              // never sign a stale hash
      const doc = (fresh.documents || []).find((d) => d.id === signing.id);
      await signDocument(id, signing.id, {
        ...form,
        documentSha256: doc ? doc.renderedSha256 : signing.renderedSha256,
        signatureImage: signMode === 'draw' ? drawnImage : null,
      });
      setSigning(null);
      closeViewer();
      Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: t('Engagements.signed', 'Signature recorded'), showConfirmButton: false, timer: 2200 });
      await load();
    } catch (err) {
      setError(apiError(err, t('Engagements.signError', 'Could not record the signature.')));
    } finally {
      setBusy(false);
    }
  };

  const decline = async (doc) => {
    const { value: reason } = await Swal.fire({
      title: t('Engagements.declineTitle', 'Decline this document?'),
      input: 'textarea', inputLabel: t('Engagements.declineReason', 'Tell Cataam what needs to change'),
      showCancelButton: true, confirmButtonText: t('Engagements.decline', 'Decline'), confirmButtonColor: '#b91c1c',
      inputValidator: (v) => (!v || v.trim().length < 5 ? t('Engagements.reasonRequired', 'Please give a reason.') : undefined),
    });
    if (!reason) return;
    try { await declineDocument(id, doc.id, reason); closeViewer(); await load(); } catch (err) { setError(apiError(err, 'Could not decline.')); }
  };

  const cancel = async () => {
    const { value: reason, isConfirmed } = await Swal.fire({
      title: t('Engagements.cancelTitle', 'Cancel this engagement?'), input: 'text',
      inputLabel: t('Engagements.cancelReason', 'Reason (optional)'), showCancelButton: true, confirmButtonColor: '#b91c1c',
    });
    if (!isConfirmed) return;
    try { await cancelEngagement(id, reason || ''); await load(); } catch (err) { setError(apiError(err, 'Could not cancel.')); }
  };

  const download = async (doc, kind) => {
    try {
      const blob = kind === 'executed' ? await fetchExecutedBlob(id, doc.id) : await fetchCertificateBlob(id, doc.id);
      downloadBlob(blob, `${e.reference}-${doc.templateCode}-${kind}.pdf`);
    } catch (err) { setError(apiError(err, 'Download failed.')); }
  };

  if (!e) return <div className="eng-page">{error ? <p className="eng-error">{error}</p> : <div className="eng-empty"><i className="fas fa-spinner fa-spin" /></div>}</div>;

  const stage = ORDER[e.status] ?? 0;
  const docs = (e.documents || []).filter((d) => d.status !== 'SUPERSEDED');
  const superseded = (e.documents || []).filter((d) => d.status === 'SUPERSEDED');
  const intake = e.intake || {};
  const canAct = canSign && ['ISSUED', 'ON_HOLD'].includes(e.status);
  const viewerDoc = viewer ? (docs.find((d) => d.id === viewer.doc.id) || viewer.doc) : null;
  const viewerCanSign = viewerDoc && canAct && ['PENDING', 'VIEWED'].includes(viewerDoc.status);

  return (
    <div className="eng-page">
      <button className="eng-btn-ghost" onClick={() => navigate('/dashboard/engagements')}><i className="fas fa-arrow-left" /> {t('Engagements.back', 'All engagements')}</button>
      <div className="eng-header" style={{ marginTop: 6 }}>
        <div>
          <h2>{e.serviceLabel} <span className="eng-card-ref" style={{ fontSize: '0.8rem' }}>{e.reference}</span></h2>
          <div className="eng-sub">{e.orgName} · {t('Engagements.requestedBy', 'requested by')} {e.requestedByEmail || '—'} · {fmtDateTime(e.requestedAt)}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <StatusPill status={e.status} />
          {canSign && !['ACTIVE', 'COMPLETED', 'CANCELLED'].includes(e.status) && (
            <button className="eng-btn-danger eng-btn-sm" onClick={cancel}>{t('Engagements.cancelEngagement', 'Cancel request')}</button>
          )}
        </div>
      </div>
      {error && <p className="eng-error">{error}</p>}

      {e.status !== 'CANCELLED' && (
        <div className="eng-stepper">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.key}>
              <div className={`eng-step ${i < stage ? 'done' : i === stage ? 'current' : ''}`}>
                <span className="dot">{i < stage ? <i className="fas fa-check" /> : i + 1}</span>{s.label}
              </div>
              {i < STEPS.length - 1 && <span className="eng-step-line" />}
            </React.Fragment>
          ))}
        </div>
      )}
      {e.status === 'REQUESTED' && (
        <div className="eng-note-box">
          <i className="fas fa-hourglass-half" /> {t('Engagements.requestedNote', 'Cataam is reviewing your intake and filling in the commercial terms (fee, dates, tester IPs). The agreements appear here as soon as they are issued; you will also be emailed.')}
        </div>
      )}
      {e.status === 'ACTIVE' && (
        <div className="eng-ok"><i className="fas fa-check-circle" /> {t('Engagements.activeNote', 'All agreements are executed. The service is active since')} {fmtDateTime(e.activatedAt)}{e.activationSource === 'WAIVED' ? ` (${t('Engagements.waived', 'activated by Cataam with a note')})` : ''}.</div>
      )}
      {e.status === 'AWAITING_COUNTERSIGN' && (
        <div className="eng-note-box"><i className="fas fa-pen-nib" /> {t('Engagements.awaitingNote', 'You have signed every document. Cataam will countersign and the service activates automatically.')}</div>
      )}
      {e.status === 'ON_HOLD' && <p className="eng-error">{t('Engagements.onHold', 'A document was declined. Cataam will review your comments and re-issue it.')}</p>}

      <div className="eng-tabs">
        {['documents', 'intake', 'timeline'].map((k) => (
          <button key={k} className={`eng-tab ${tab === k ? 'eng-tab-on' : ''}`} onClick={() => setTab(k)}>
            {k === 'documents' ? t('Engagements.tabDocs', 'Documents') : k === 'intake' ? t('Engagements.tabIntake', 'Intake') : t('Engagements.tabTimeline', 'Audit timeline')}
          </button>
        ))}
      </div>

      {tab === 'documents' && (
        <div className="eng-panel">
          {docs.length === 0 ? (
            <div className="eng-empty">{t('Engagements.noDocsYet', 'No documents yet. They appear here once Cataam issues them.')}</div>
          ) : (
            <table className="eng-table">
              <thead><tr><th>{t('Engagements.colDoc', 'Document')}</th><th>{t('Engagements.colStatus', 'Status')}</th><th>{t('Engagements.colSigned', 'Signatures')}</th><th /></tr></thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{d.templateName}</div>
                      <div className="eng-help">{d.templateCode} v{d.versionNo}{d.required ? '' : ` · ${t('Engagements.optional', 'optional')}`}</div>
                      <div className="eng-mono" title="SHA-256 of the PDF you sign">{(d.renderedSha256 || '').slice(0, 16)}…</div>
                    </td>
                    <td><StatusPill status={d.status} labels={DOC_STATUS_LABELS} /></td>
                    <td>
                      {(d.signatures || []).map((s) => (
                        <div key={s.id} style={{ fontSize: '0.76rem' }}>
                          <i className={`fas ${s.action === 'SIGNED' ? 'fa-pen-nib' : 'fa-ban'}`} style={{ color: s.action === 'SIGNED' ? '#166534' : '#b91c1c', marginRight: 4 }} />
                          {s.party === 'CATAAM' ? 'Cataam' : t('Engagements.client', 'Client')}: {s.typedName} · {fmtDateTime(s.signedAt)}
                        </div>
                      ))}
                    </td>
                    <td>
                      <div className="eng-doc-actions">
                        <button className={canAct && ['PENDING', 'VIEWED'].includes(d.status) ? 'eng-btn-primary eng-btn-sm' : 'eng-btn eng-btn-sm'} onClick={() => view(d)} disabled={opening === d.id}>
                          <i className={`fas ${opening === d.id ? 'fa-spinner fa-spin' : 'fa-file-pdf'}`} /> {canAct && ['PENDING', 'VIEWED'].includes(d.status) ? t('Engagements.reviewSign', 'Review & sign') : d.hasExecutedCopy ? t('Engagements.openExecuted', 'Open signed copy') : t('Engagements.view', 'Open')}
                        </button>
                        {d.hasExecutedCopy && <button className="eng-btn eng-btn-sm" onClick={() => download(d, 'executed')}><i className="fas fa-download" /> {t('Engagements.executed', 'Executed copy')}</button>}
                        {d.hasExecutedCopy && <button className="eng-btn-ghost eng-btn-sm" title={t('Engagements.originalHelp', 'The exact bytes that were signed; their SHA-256 is what the certificate records')} onClick={() => view(d, true)}>{t('Engagements.original', 'Original (as signed)')}</button>}
                        {(d.signatures || []).length > 0 && <button className="eng-btn eng-btn-sm" onClick={() => download(d, 'certificate')}><i className="fas fa-certificate" /> {t('Engagements.certificate', 'Certificate')}</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {superseded.length > 0 && <div className="eng-help" style={{ marginTop: 8 }}>{superseded.length} {t('Engagements.supersededNote', 'earlier version(s) were superseded and remain in the audit trail.')}</div>}
        </div>
      )}

      {tab === 'intake' && (
        <div className="eng-two-col">
          <div className="eng-panel">
            <h3>{t('Engagements.scope', 'Scope')}</h3>
            <table className="eng-table"><thead><tr><th>#</th><th>Asset</th><th>Reference</th><th>Env.</th><th>Stack</th></tr></thead>
              <tbody>{(intake.assets || []).map((a, i) => <tr key={i}><td>{i + 1}</td><td>{a.asset}</td><td>{a.reference}</td><td>{a.env}</td><td>{a.stack}</td></tr>)}</tbody></table>
            <h3 style={{ marginTop: 14 }}>{t('Engagements.contactsShort', 'Contacts')}</h3>
            {(intake.contacts || []).map((c, i) => <div key={i} style={{ fontSize: '0.82rem' }}>{c.role}: <strong>{c.name}</strong> · {c.phone} · {c.email}</div>)}
          </div>
          <div className="eng-panel">
            <h3>{t('Engagements.preferences', 'Testing preferences')}</h3>
            <dl className="eng-kv">
              {['environment', 'hasRealData', 'backupNote', 'testingApproach', 'exploitationDepth', 'realRecordsAccess', 'methodology', 'severityScheme', 'reportAudience', 'testingWindows', 'fragileComponents', 'testAccounts', 'wafChanges', 'outOfScope', 'sourceCodeRepo', 'apiSpecs', 'secureChannel', 'preferredStartDate', 'notes']
                .filter((k) => intake[k] !== undefined && intake[k] !== '' && intake[k] !== null)
                .map((k) => <React.Fragment key={k}><dt>{k}</dt><dd>{typeof intake[k] === 'boolean' ? (intake[k] ? 'Yes' : 'No') : String(intake[k])}</dd></React.Fragment>)}
            </dl>
            {e.party && (
              <>
                <h3 style={{ marginTop: 14 }}>{t('Engagements.legalEntity', 'Legal entity (as issued)')}</h3>
                <div style={{ fontSize: '0.82rem' }}>{e.party.legalName}{e.party.tradingName ? ` (trading as ${e.party.tradingName})` : ''}<br />{e.party.address}<br />{t('Engagements.signatory', 'Signatory')}: {e.party.signatoryName}, {e.party.signatoryTitle}</div>
              </>
            )}
          </div>
        </div>
      )}

      {tab === 'timeline' && (
        <div className="eng-panel">
          <ul className="eng-timeline">
            {events.map((ev) => (
              <li key={ev.id}>
                <div className="when">{fmtDateTime(ev.occurredAt)} UTC</div>
                <div>
                  <div className="what">{ev.eventType.replace(/_/g, ' ')}{ev.toStatus && ev.fromStatus !== ev.toStatus ? ` → ${ev.toStatus}` : ''}{ev.details ? ` — ${ev.details}` : ''}</div>
                  <div className="who">{ev.actorType === 'PLATFORM_STAFF' ? 'Cataam staff' : ev.actorType === 'SYSTEM' ? 'System' : (ev.actorEmail || 'User')}{ev.ipAddress ? ` · ${ev.ipAddress}` : ''}</div>
                </div>
              </li>
            ))}
            {events.length === 0 && <li><div /><div className="who">{t('Engagements.noEvents', 'No events yet.')}</div></li>}
          </ul>
        </div>
      )}

      {viewer && viewerDoc && (
        <div className="eng-overlay" onClick={closeViewer}>
          <div className="eng-modal eng-modal-viewer" onClick={(ev) => ev.stopPropagation()}>
            <div className="eng-viewer-head">
              <div>
                <h3 style={{ margin: 0 }}>{viewerDoc.templateName}</h3>
                <div className="eng-sub" style={{ margin: 0 }}>{viewerDoc.templateCode} v{viewerDoc.versionNo} · <StatusPill status={viewerDoc.status} labels={DOC_STATUS_LABELS} /></div>
              </div>
              <div className="eng-doc-actions">
                <button className="eng-btn eng-btn-sm" onClick={() => window.open(viewer.url, '_blank', 'noopener')}><i className="fas fa-external-link-alt" /> {t('Engagements.openTab', 'Open in new tab')}</button>
                <button className="eng-btn eng-btn-sm" onClick={() => downloadBlob(viewer.blob, `${e.reference}-${viewerDoc.templateCode}.pdf`)}><i className="fas fa-download" /> {t('Engagements.download', 'Download')}</button>
                <button className="eng-btn-ghost eng-btn-sm" onClick={closeViewer}><i className="fas fa-times" /> {t('common.close', 'Close')}</button>
              </div>
            </div>
            <object data={viewer.url} type="application/pdf" className="eng-viewer-frame" aria-label="Agreement preview">
              <div className="eng-empty">
                {t('Engagements.noInlinePdf', 'Your browser cannot show PDFs inline.')} <button className="eng-btn-ghost" onClick={() => window.open(viewer.url, '_blank', 'noopener')}>{t('Engagements.openTab', 'Open in new tab')}</button>
              </div>
            </object>
            {viewerCanSign && (
              <div className="eng-modal-foot">
                <span className="eng-help">{t('Engagements.readThenSign', 'Read the whole document, then sign or decline.')}</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="eng-btn-danger" onClick={() => decline(viewerDoc)}>{t('Engagements.decline', 'Decline')}</button>
                  <button className="eng-btn-primary" onClick={() => openSign(viewerDoc)}><i className="fas fa-signature" /> {t('Engagements.sign', 'Sign this document')}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {signing && (
        <div className="eng-overlay" onClick={() => !busy && setSigning(null)}>
          <div className="eng-modal eng-modal-sm" onClick={(ev) => ev.stopPropagation()}>
            <h3>{t('Engagements.signTitle', 'Sign')}: {signing.templateName}</h3>
            <div className="eng-sub">{signing.templateCode} v{signing.versionNo} · SHA-256 <span className="eng-mono">{(signing.renderedSha256 || '').slice(0, 24)}…</span></div>
            <div className="eng-form" style={{ marginTop: 14 }}>
              <div className="eng-field full">
                <label>{t('Engagements.typedName', 'Type your full legal name *')}</label>
                <input value={form.typedName} onChange={(ev) => setForm({ ...form, typedName: ev.target.value })} autoFocus />
              </div>
              <div className="eng-field full">
                <label>{t('Engagements.typedTitle', 'Your title')}</label>
                <input value={form.typedTitle} onChange={(ev) => setForm({ ...form, typedTitle: ev.target.value })} />
              </div>
            </div>
            <div className="eng-mode" role="tablist">
              <button type="button" className={signMode === 'type' ? 'on' : ''} onClick={() => setSignMode('type')}><i className="fas fa-keyboard" /> {t('Engagements.modeType', 'Type')}</button>
              <button type="button" className={signMode === 'draw' ? 'on' : ''} onClick={() => setSignMode('draw')}><i className="fas fa-pen-fancy" /> {t('Engagements.modeDraw', 'Draw')}</button>
            </div>
            {signMode === 'type'
              ? <div className="eng-typed-preview">{form.typedName || ' '}</div>
              : <SignaturePad onChange={setDrawnImage} disabled={busy} />}
            <label className="eng-consent">
              <input type="checkbox" checked={form.consent} onChange={(ev) => setForm({ ...form, consent: ev.target.checked })} />
              <span>{e.clientConsentText}</span>
            </label>
            <div className="eng-help">{t('Engagements.signNote', 'Your name, email, role, IP address, device and the document hash are recorded in an immutable signature certificate.')}{signMode === 'draw' ? ' ' + t('Engagements.drawNote', 'Your drawn signature is stored with it and placed on the signature line of the executed copy.') : ''}</div>
            <div className="eng-modal-foot">
              <button className="eng-btn" onClick={() => setSigning(null)} disabled={busy}>{t('common.cancel', 'Cancel')}</button>
              <button className="eng-btn-primary" onClick={submitSign} disabled={busy || !form.consent || form.typedName.trim().length < 2 || (signMode === 'draw' && !drawnImage)}>
                <i className="fas fa-signature" /> {busy ? t('Engagements.signing', 'Signing…') : t('Engagements.confirmSign', 'Sign document')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
